"use client";

import React from "react";

import { PitchRecommendationAutoButton } from "@/app/(application)/(normal)/create/components/input/PitchRecommendationAutoButton";
import { PitchSlider } from "@/app/(application)/(normal)/create/components/input/PitchSlider";
import { Slider } from "@/components/form/slider/Slider";
import { ImportantText } from "@/components/ImportantText";
import { Heading4 } from "@/components/text/Heading4";
import { Label } from "@/components/text/Label";
import { SoftText } from "@/components/text/SoftText";
import { PITCH_SLIDER_MAX, PITCH_SLIDER_MIN } from "@/constants/audioQuality";
import { useConversionHyperParameters } from "@/lib/zustand/useConversionHyperParameters";
import { PitchRecommendation } from "@/types/defs/audioQuality/pitchRecommendation";
import { UserNoDates } from "@/types/types";
import { isSinglePitch } from "@/utils/isSinglePitch";

export function HyperParameters({
  isInstrumentSelected,
  user,
  quickTuningKnobs,
  pitchEditorButton,
  hideEditPitchStuff,
  shouldShowLoadState,
  pitchRecommendation,
}: {
  isInstrumentSelected: boolean;
  user: UserNoDates | null;
  quickTuningKnobs: React.ReactNode;
  pitchEditorButton: React.ReactNode;
  hideEditPitchStuff: boolean;
  shouldShowLoadState: boolean;
  pitchRecommendation: PitchRecommendation;
}) {
  const pitch = useConversionHyperParameters((state) => state.pitch);
  const setPitch = useConversionHyperParameters((state) => state.setPitch);
  const conversionStrength = useConversionHyperParameters(
    (state) => state.conversionStrength
  );
  const setConversionStrength = useConversionHyperParameters(
    (state) => state.setConversionStrength
  );

  return (
    <div className="mt-10">
      {!hideEditPitchStuff && (
        <div>
          <Heading4 elementType="h3" className="mb-4 w-fit">
            Edit pitch
          </Heading4>

          <Label elementType="label" className="mb-1">
            Quick tuning
          </Label>
          <SoftText
            elementType="p"
            className="mb-2 text-sm font-normal text-gray-500"
          >
            Use the quick tuning for a fast tuning. Make sure to set the key!
          </SoftText>

          {quickTuningKnobs}

          <Label elementType="label" className="mb-1 mt-6">
            Pitch editor
          </Label>
          <SoftText
            elementType="p"
            className="mb-2 text-sm font-normal text-gray-500"
          >
            Use the pitch editor for manual tuning, for creating harmonies or
            changing melody.
          </SoftText>
          {pitchEditorButton}
        </div>
      )}

      <div className="mt-6 border-t border-gray-900/5 pt-6">
        <Heading4 elementType="h3" className="mb-4">
          Settings
        </Heading4>

        {isInstrumentSelected && (
          <ImportantText className="mb-4">
            Instruments usually benefit from a high conversion strength.
          </ImportantText>
        )}

        <PitchSlider
          sliderProps={{
            disabled: shouldShowLoadState,
            value: isSinglePitch(pitch) ? [pitch] : pitch,
            min: PITCH_SLIDER_MIN,
            max: PITCH_SLIDER_MAX,
            step: 1,
            onValueChange: (pitchApp) => {
              setPitch(
                pitchApp.length === 2 ? [pitchApp[0], pitchApp[1]] : pitchApp[0]
              );
            },
          }}
          className="mb-2"
          pitchRecommendation={pitchRecommendation}
        />

        <PitchRecommendationAutoButton
          pitchRecommendation={pitchRecommendation}
        />

        <Slider
          label="Conversion strength"
          description="Decrease to retain more characteristics and articulation from the input, useful when the input is non-English. Higher values may introduce artifacts."
          sliderProps={{
            disabled: shouldShowLoadState,
            value: [conversionStrength],
            min: 0,
            max: 1,
            step:
              (
                user !== null &&
                user.dbId === "e75eb4f6-ac56-4596-81c7-32302cd79954"
              ) ?
                0.01
              : 0.1,
            onValueChange: ([newConversionStrength]) =>
              setConversionStrength(newConversionStrength),
            trackStyle: null,
          }}
          decimalToPercent={true}
          className="mb-6"
        />
      </div>
    </div>
  );
}
